import type { JSX } from 'solid-js'
import { createEffect, createMemo, createSignal, createUniqueId, mergeProps, onCleanup } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Icon from '~/components/Icon'
import { SolarAltArrowRightLinear } from '~/components/icons'
import { useAccordionContext } from '~/contexts/AccordionContext'
import type { AccordionColorType, AccordionPropsType, AccordionVariantType } from '~/types/Accordion'
import { getGsap, MOTION, prefersReducedMotion } from '~/utils/gsap'

const colorClasses = {
  primary: 'text-primary',
  secondary: 'text-foreground-subtle',
  success: 'text-success',
  danger: 'text-danger',
  warning: 'text-warning',
  info: 'text-info',
  default: 'text-foreground-subtle'
}

const variantClasses: Record<AccordionVariantType, string> = {
  default: 'bg-surface-muted border-border',
  elevation: 'bg-surface-muted shadow-md border-border',
  outlined: 'bg-transparent border-2 border-black/20 dark:border-white/20'
}

export default function Accordion(props: AccordionPropsType): JSX.Element {
  const context = useAccordionContext()
  const id = createUniqueId()

  const merged = mergeProps(
    {
      color: 'default' as AccordionColorType,
      open: false,
      variant: 'default',
      indicator: SolarAltArrowRightLinear,
      icon: undefined as string | undefined
    },
    props
  )

  const [internalIsOpen, setInternalIsOpen] = createSignal(merged.open)
  const contentId = `accordion-content-${id}`
  const headerId = `accordion-header-${id}`
  let contentRef: HTMLDivElement | undefined

  const valueId = merged.value ?? id

  const isOpen = () => {
    if (context) {
      return context.isOpen(valueId)
    } else {
      return props.open !== undefined ? merged.open : internalIsOpen()
    }
  }

  const indicatorClasses = createMemo(() => twMerge('transition-transform duration-200', isOpen() ? 'rotate-90' : ''))

  createEffect(() => {
    if (!context) {
      setInternalIsOpen(merged.open)
    }
  })

  let firstRun = true
  createEffect(() => {
    const open = isOpen()
    if (!contentRef) return
    const g = getGsap()
    // Primer render o movimiento reducido: fijamos altura sin animar.
    if (firstRun || !g || prefersReducedMotion()) {
      contentRef.style.height = open ? 'auto' : '0px'
      firstRun = false
      return
    }
    g.killTweensOf(contentRef)
    if (open) {
      g.to(contentRef, { height: 'auto', duration: 0.25, ease: MOTION.ease })
    } else {
      g.to(contentRef, { height: 0, duration: 0.2, ease: MOTION.ease })
    }
  })

  onCleanup(() => {
    const g = getGsap()
    if (contentRef && g) g.killTweensOf(contentRef)
    if (contentRef) contentRef.style.height = ''
  })

  const toggleAccordion = () => {
    if (context) {
      context.toggle(valueId)
    } else {
      const newState = !isOpen()
      if (props.open === undefined) {
        setInternalIsOpen(newState)
      }

      merged.onToggle?.(newState)
    }
  }

  const accordionClasses = createMemo(() => twMerge('rounded-lg', variantClasses[(merged.variant ?? 'default') as AccordionVariantType], merged.class))
  const buttonClasses = createMemo(() =>
    twMerge(
      `flex
    items-center
    justify-between
    w-full
    p-4
    cursor-pointer
    rounded-lg
    font-medium
    bg-surface-muted
    hover:bg-surface-hover
    gap-3
    `,
      merged.variant === 'outlined' ? 'border-0 bg-transparent hover:bg-transparent' : '',
      merged.titleClass
    )
  )
  const contentClasses = createMemo(() => twMerge(`overflow-hidden ${isOpen() ? '' : 'pointer-events-none'}`))
  const innerContentClasses = createMemo(() => twMerge('p-4 border-t border-border', merged.contentClass))

  return (
    <div role="region" aria-labelledby={headerId} class={accordionClasses()} classList={merged.classList}>
      <h2 id={headerId} class="m-0">
        <button type="button" class={buttonClasses()} onClick={toggleAccordion} aria-expanded={isOpen()} aria-controls={contentId} tabIndex={0}>
          <div class="flex items-center gap-3">
            {merged.icon && <Icon icon={merged.icon} size={24} class={twMerge(colorClasses[merged.color])} aria-hidden="true" />}
            <div class="flex flex-col text-left">
              <span class="text-foreground">{merged.title}</span>
              {merged.subtitle && <span class="text-sm font-normal text-foreground-subtle">{merged.subtitle}</span>}
            </div>
          </div>
          {(() => {
            const indicator = merged.indicator
            if (typeof indicator === 'string') {
              return <Icon icon={indicator} class={indicatorClasses()} aria-hidden="true" />
            } else {
              return indicator({ class: indicatorClasses(), 'aria-hidden': 'true' })
            }
          })()}
        </button>
      </h2>
      <div
        id={contentId}
        ref={el => {
          contentRef = el
          if (contentRef && isOpen()) contentRef.style.height = 'auto'
        }}
        class={contentClasses()}
        aria-labelledby={headerId}
        style="height:0;"
      >
        <div class={innerContentClasses()}>{merged.children}</div>
      </div>
    </div>
  )
}
